import { Box, Flex, chakra, Button, RadioGroup, Stack, Radio, FormLabel, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { generateArrayOfYears, showErrorToast } from "../../../../../utils";
import { useTranslation } from "react-i18next";
import { FormMultiSelect, FormSelect } from "../../../../../components/Forms";
import * as React from "react";
import { useState } from "react";
import { useExportConsumptionsData } from "../../../../../hooks/query/reports";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";

const REPORT_BY_OPTIONS = Object.freeze({
  "YEAR": "YEAR",
  "QUARTER": "QUARTER"
});

function ConsumptionsData({ organizationStructure }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      dimension: null,
      years: []
    }
  });

  const [reportBy, setReportBy] = useState(REPORT_BY_OPTIONS.YEAR);

  const { mutate: exportConsumptionsData } = useExportConsumptionsData({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const onSubmit = (data) => {
    exportConsumptionsData({
      dimensionId: parseInt(data.dimension),
      queryParams: `groupByYears=${reportBy === REPORT_BY_OPTIONS.YEAR}&${data.years.map(year => `years=${year.value}`).join("&")}`
    });
  };

  return (
    <Box>
      <chakra.form id="consumptionsDataForm" onSubmit={handleSubmit(onSubmit)}>
        <Flex gap="4" marginBlockStart="5" alignItems="end" justifyContent="space-between">
          <Flex width="75%" gap="4">
            <FormSelect
              label={`${t("inputs.dimension")} *`}
              errors={errors}
              {...register("dimension", {
                required: globalTranslation("entityRequired", { entityName: t("inputs.dimension") })
              })}
              placeholder={" "}
            >
              {organizationStructure ? Object.keys(organizationStructure.dimensions)
                .filter(key => organizationStructure.dimensions[key] !== null)
                .sort((a, b) => {
                  return (organizationStructure.dimensions[a].id > organizationStructure.dimensions[b].id) ? 1 :
                    ((organizationStructure.dimensions[b].id > organizationStructure.dimensions[a].id) ? -1 : 0);
                })
                .map(dimKey => {
                  return <option
                    key={`dim-key-${organizationStructure.dimensions[dimKey].id}`}
                    value={organizationStructure.dimensions[dimKey].id}
                  >
                    {organizationStructure.dimensions[dimKey].name}
                  </option>;
                }) : []
              }
            </FormSelect>
            <FormMultiSelect
              control={control}
              name="years"
              id="years"
              options={generateArrayOfYears().map((year) => {
                return {
                  label: year,
                  value: year
                };
              })}
              placeholder={""}
              label={`${t("inputs.reportPeriods")} *`}
              rules={{ required: globalTranslation("entityRequired", { entityName: `${t("inputs.reportPeriods")} field` }) }}
              errors={errors}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Box>
              <FormLabel
                htmlFor={"reportByRadioGroup"}
                opacity="0.75"
                fontSize="xs"
                whiteSpace="nowrap"
                marginBlockEnd="0"
                blockSize="5"
                isTruncated
                color="esg_gray.200"
                title={`${t("inputs.reportBy")} *`}
              >
                {`${t("inputs.reportBy")} *`}
              </FormLabel>
              <RadioGroup
                id={"reportByRadioGroup"}
                value={reportBy}
                style={{ position: "relative" }}
                minHeight={"40px"}
                display={"flex"}
                alignItems={"center"}
              >
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value={REPORT_BY_OPTIONS.YEAR}
                    onChange={e => {
                      setReportBy(e?.target.value);
                    }}
                  >
                    {t("inputs.year")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value={REPORT_BY_OPTIONS.QUARTER}
                    onChange={e => {
                      setReportBy(e?.target.value);
                    }}
                  >
                    {t("inputs.quarter")}
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
          </Flex>
          <Button
            colorScheme="customScheme"
            width="32"
            type="submit"
          >
            {t("buttons.export")}
          </Button>
        </Flex>
      </chakra.form>
    </Box>
  );
}

export default ConsumptionsData;